import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { usePublicPageMetadata } from '../../hooks/usePublicPageMetadata';
import { NotFoundPage, PageUnavailable } from './PageStatus';

import PublicHeader from '../PublicHeader';
import ComponentTypeListLinks from '../ComponentTypeListLinks';
import ComponentAnalyticsTracker from '../ComponentAnalyticsTracker';
import MuultiSpaceLogFeed from './MuultiSpaceLogFeed';
import { renderPublicMuultiComponent } from '../renderPublicMuultiComponent';
import {
    getComponentTypeListOption,
    normalizeComponentListSettings,
    normalizeListComponentType,
} from '../../utils/ComponentTypeLists';
import '../MuultiStyle.css';
import { choosePreferredUserSnapshot, isComponentListAvailable } from '../../../functions/publicMetadataPolicy.js';

const toPositiveInt = (value, fallback = 1) => {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return fallback;
};

const extractUrlFromCssBackgroundImage = (bgImage) => {
    if (!bgImage || typeof bgImage !== 'string') return '';
    const match = bgImage.match(/url\((['"]?)(.*?)\1\)/i);
    return match?.[2] || '';
};

const normalizeImageUrl = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^url\(/i.test(trimmed)) return extractUrlFromCssBackgroundImage(trimmed);
    return trimmed;
};

const buildPageStyle = (background) => {
    const bg = background || { type: 'single', color: '#ffffff' };
    const imageUrl =
        normalizeImageUrl(bg.imageUrl) ||
        normalizeImageUrl(bg.url) ||
        normalizeImageUrl(bg.image) ||
        normalizeImageUrl(bg.src) ||
        extractUrlFromCssBackgroundImage(bg.backgroundImage) ||
        extractUrlFromCssBackgroundImage(bg.background) ||
        '';
    const normalizedType = (() => {
        const rawType = typeof bg.type === 'string' ? bg.type.toLowerCase() : bg.type;
        if (rawType === 'cover') return 'image';
        if (rawType) return rawType;
        if (imageUrl) return 'image';
        if (Array.isArray(bg.colors) && bg.colors.length >= 2) return 'gradient';
        if (bg.color || bg.backgroundColor) return 'single';
        return 'none';
    })();

    if (normalizedType === 'single') {
        return { backgroundColor: bg.color || bg.backgroundColor || '#ffffff' };
    }
    if (normalizedType === 'gradient') {
        const colors = Array.isArray(bg.colors) && bg.colors.length >= 2
            ? bg.colors
            : ['#ffffff', '#000000'];
        const direction = bg.direction || 'to right';
        return { backgroundImage: `linear-gradient(${direction}, ${colors[0]}, ${colors[1]})` };
    }
    if (normalizedType === 'image') {
        return {
            backgroundColor: bg.backgroundColor || bg.color || '#ffffff',
            backgroundImage: imageUrl ? `url("${imageUrl}")` : undefined,
            backgroundSize: bg.sizing || bg.backgroundSize || 'cover',
            backgroundPosition: bg.position || bg.backgroundPosition || 'center',
            backgroundRepeat: bg.repeatMode || bg.backgroundRepeat || 'no-repeat',
        };
    }
    return {};
};

const buildHeaderStyle = (headerBackground) => {
    const hb = headerBackground || {
        type: 'color',
        color: '#ffffff',
        colors: ['#ffffff', '#000000'],
        fontColor: '#000000',
        direction: 'to right',
        imageUrl: '',
        sizing: 'cover',
        position: 'center',
    };

    let base;
    const headerImageUrl =
        normalizeImageUrl(hb.imageUrl) ||
        normalizeImageUrl(hb.url) ||
        normalizeImageUrl(hb.image) ||
        normalizeImageUrl(hb.src) ||
        extractUrlFromCssBackgroundImage(hb.backgroundImage) ||
        extractUrlFromCssBackgroundImage(hb.background) ||
        '';
    const headerRawType = typeof hb.type === 'string' ? hb.type.toLowerCase() : hb.type;
    const headerType = headerRawType === 'cover' ? 'image' : headerRawType;

    switch (headerType) {
        case 'color':
            base = { backgroundColor: hb.color };
            break;
        case 'gradient': {
            const colors = Array.isArray(hb.colors) && hb.colors.length >= 2
                ? hb.colors
                : ['#ffffff', '#000000'];
            base = {
                backgroundImage: `linear-gradient(${hb.direction || 'to right'}, ${colors[0]}, ${colors[1]})`,
            };
            break;
        }
        case 'image':
            base = {
                backgroundColor: hb.fallback || hb.backgroundColor || hb.color || '#ffffff',
                backgroundImage: headerImageUrl ? `url("${headerImageUrl}")` : undefined,
                backgroundSize: hb.sizing || hb.backgroundSize || 'cover',
                backgroundPosition: hb.position || hb.backgroundPosition || 'center',
                backgroundRepeat: hb.repeatMode || hb.backgroundRepeat || 'no-repeat',
            };
            break;
        default:
            base = {};
    }

    return {
        ...base,
        color: hb.fontColor,
        borderStyle: hb.borderStyle || 'none',
        borderWidth: hb.borderWidth ?? 1,
        borderColor: hb.borderColor || 'transparent',
        borderRadius: hb.borderRadius ?? undefined,
        boxSizing: 'border-box',
        backgroundOrigin: 'border-box',
        backgroundClip: 'border-box',
    };
};

const buildContainerStyle = (muulti) => ({
    borderStyle: muulti.containerFrame?.borderStyle,
    borderWidth: muulti.containerFrame?.borderWidth ?? undefined,
    borderColor: muulti.containerFrame?.borderColor,
    borderRadius: muulti.containerFrame?.borderRadius ?? undefined,
    paddingTop: muulti.containerFrame?.paddingTop ?? undefined,
    paddingRight: muulti.containerFrame?.paddingRight ?? undefined,
    paddingBottom: muulti.containerFrame?.paddingBottom ?? undefined,
    paddingLeft: muulti.containerFrame?.paddingLeft ?? undefined,
    boxSizing: 'border-box',
});

export default function ComponentTypeListPage() {
    const { username, componentType } = useParams();
    const [muulti, setMuulti] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    const requestedType = normalizeListComponentType(componentType);
    const typeOption = getComponentTypeListOption(requestedType);
    const isPostsList = requestedType === 'posts';

    useEffect(() => {
        (async () => {
            setLoading(true);
            setLoadError(false);
            try {
                const usersRef = collection(db, 'users');
                let data = null;

                const q = query(usersRef, where('muultiName', '==', username));
                const snap = await getDocs(q);
                const preferredDoc = choosePreferredUserSnapshot(snap.docs);
                data = preferredDoc ? (preferredDoc.data() || {}) : null;

                setMuulti(data || undefined);
            } catch (e) {
                console.error(e);
                setLoadError(true);
                setMuulti(undefined);
            } finally {
                setLoading(false);
            }
        })();
    }, [username]);

    const filteredComponents = useMemo(() => {
        if (requestedType === 'posts') return [];
        if (!requestedType || !muulti || !Array.isArray(muulti.components)) return [];
        return muulti.components.filter((comp) => normalizeListComponentType(comp?.type) === requestedType);
    }, [muulti, requestedType]);

    const isAvailable = isComponentListAvailable(muulti, requestedType);
    usePublicPageMetadata(muulti, loading || loadError, isAvailable);

    if (loading) return <div className="muulti-container"><p>Loading...</p></div>;
    if (loadError) return <PageUnavailable />;
    if (!requestedType || !typeOption) return <NotFoundPage />;
    if (!muulti || !isAvailable) return <NotFoundPage />;

    const settings = normalizeComponentListSettings(muulti.componentListSettings);
    const template = muulti.template || '';
    const pageStyle = buildPageStyle(muulti.background);
    const headerStyleProps = buildHeaderStyle(muulti.headerBackground);
    const containerStyle = buildContainerStyle(muulti);
    const safeUsername = muulti.muultiName || username || '';
    const ownerPublicKey = String(muulti.publicKey || '').trim();

    const publicPagePath = `/${safeUsername}`;
    const gridGap = muulti.gridGap != null ? muulti.gridGap : 24;
    const safeMinColumnWidthPx = Math.max(80, toPositiveInt(muulti.minWidthPx, 400));
    const columnsCount = muulti.columnsCount || 'auto-fill';
    const explicitColumnsCount = (() => {
        const parsed = Number.parseInt(columnsCount, 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
        return null;
    })();
    const componentGridTemplateColumns = explicitColumnsCount != null
        ? `repeat(${explicitColumnsCount}, minmax(${safeMinColumnWidthPx}px, 1fr))`
        : `repeat(auto-fill, minmax(${safeMinColumnWidthPx}px, 1fr))`;
    const componentTypeListLinks = (
        <ComponentTypeListLinks
            components={muulti.components || []}
            componentListSettings={settings}
            publicKey={ownerPublicKey}
            username={safeUsername}
            currentType={requestedType}
            homePath={publicPagePath}
            showFollowButton
            showFollowerCount={muulti.showFollowerCount === true}
        />
    );
    const componentTypeListLinksInHeader = settings.placement === 'inHeader';

    return (
        <div className={`muulti-container ${template}`} style={{ ...pageStyle, width: '100%', ...containerStyle }}>
            <PublicHeader
                profilePicUrl={muulti.profilePicUrl}
                pageTitle={muulti.pageTitle || muulti.muultiName}
                pageDescription={muulti.pageDescription}
                alignment={muulti.alignment}
                headerPadding={muulti.headerPadding}
                style={headerStyleProps}
            >
                {componentTypeListLinksInHeader ? componentTypeListLinks : null}
            </PublicHeader>

            {!componentTypeListLinksInHeader ? componentTypeListLinks : null}

            {isAvailable ? (
                isPostsList ? (
                    <MuultiSpaceLogFeed
                        authorUid={ownerPublicKey}
                        hideComposer
                        embedded
                        feedTitle={`${safeUsername} Social Posts`}
                        emptyMessageOverride={`${safeUsername} has not posted yet.`}
                        backLinkPath={publicPagePath}
                        backLinkLabel={`Back to ${safeUsername}`}
                    />
                ) : (
                <>
                    <div className="component-type-list-heading">
                        <h2>{typeOption.listLabel}</h2>
                    </div>

                    <div
                        className="public-components component-type-list-grid"
                        style={{
                            gridTemplateColumns: componentGridTemplateColumns,
                            gridGap: `${gridGap}px`,
                            width: '100%',
                            minWidth: 0,
                        }}
                    >
                        {filteredComponents.map((comp, idx) => {
                            const componentNode = renderPublicMuultiComponent({
                                comp,
                                muulti,
                                commonProps: {
                                    comp,
                                    index: idx,
                                    style: comp.style || {},
                                    pageTypography: muulti.typography || null,
                                    isEditor: false,
                                    ownerPublicKey: muulti.publicKey || '',
                                    ownerUsername: safeUsername,
                                },
                            });
                            if (!componentNode) return null;
                            const panelId = String(comp?.id ?? idx);
                            return (
                                <div
                                    key={panelId}
                                    id={`public-list-panel-${panelId}`}
                                    className="component-grid-item"
                                    style={{ minWidth: 0 }}
                                >
                                    <ComponentAnalyticsTracker
                                        comp={comp}
                                        muulti={muulti}
                                        surface="list"
                                    >
                                        {componentNode}
                                    </ComponentAnalyticsTracker>
                                </div>
                            );
                        })}
                    </div>
                </>
                )
            ) : (
                <div className="component-type-list-heading">
                    <h2>{typeOption.listLabel}</h2>
                    <p>This list page is not available.</p>
                </div>
            )}

            <div className="detail-page-return-link-container">
                <Link className="muulti-button detail-page-return-link" to={publicPagePath}>
                    Back to {safeUsername}
                </Link>
            </div>
        </div>
    );
}
